import {
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  Cpu,
  HardDrive,
  MemoryStick,
  Users,
  RefreshCw,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useQueueDashboard, useQueueHealth } from '@/hooks/useQueue'
import { formatDuration } from '@/lib/utils'
import { cn } from '@/lib/utils'

export function QueuePage() {
  const { data, isLoading, error, refetch, isFetching } = useQueueDashboard()
  const { data: health } = useQueueHealth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fila de Processamento</h1>
          <p className="text-muted-foreground">
            Status do whisper-processor em tempo real
          </p>
        </div>
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-4 py-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="font-medium">Erro ao conectar ao whisper-processor</p>
              <p className="text-sm text-muted-foreground">
                Verifique se o serviço está em execução
              </p>
            </div>
            <Button variant="outline" className="ml-auto" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const activeConferences = data?.active_conferences || []
  const processingSessions = data?.processing_sessions || []
  const waitingSessions = data?.waiting_sessions || []
  const recentSessions = data?.recent_sessions || []
  const systemStats = data?.system_stats
  const workerStats = data?.worker_stats

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fila de Processamento</h1>
          <p className="text-muted-foreground">
            Status do whisper-processor em tempo real
          </p>
        </div>
        <div className="flex items-center gap-4">
          {health?.status === 'healthy' ? (
            <Badge variant="success" className="gap-1">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Online
            </Badge>
          ) : (
            <Badge variant="destructive" className="gap-1">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              Offline
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', isFetching && 'animate-spin')} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Activity className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeConferences.length}</p>
              <p className="text-xs text-muted-foreground">Conferências Ativas</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="p-3 rounded-lg bg-yellow-500/10">
              <Clock className="h-6 w-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{processingSessions.length}</p>
              <p className="text-xs text-muted-foreground">Em Processamento</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="p-3 rounded-lg bg-orange-500/10">
              <Clock className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{waitingSessions.length}</p>
              <p className="text-xs text-muted-foreground">Aguardando</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{recentSessions.length}</p>
              <p className="text-xs text-muted-foreground">Recentes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Stats */}
      {(systemStats || workerStats) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              {systemStats && (
                <>
                  <div className="flex items-center gap-3">
                    <Cpu className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">CPU</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full w-24">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              systemStats.cpu_percent > 80 ? 'bg-red-500' : 'bg-primary'
                            )}
                            style={{ width: `${systemStats.cpu_percent}%` }}
                          />
                        </div>
                        <span className="text-sm">{systemStats.cpu_percent.toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MemoryStick className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Memória</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full w-24">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              systemStats.memory_percent > 80 ? 'bg-red-500' : 'bg-primary'
                            )}
                            style={{ width: `${systemStats.memory_percent}%` }}
                          />
                        </div>
                        <span className="text-sm">{systemStats.memory_percent.toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <HardDrive className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Disco</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full w-24">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              systemStats.disk_percent > 80 ? 'bg-red-500' : 'bg-primary'
                            )}
                            style={{ width: `${systemStats.disk_percent}%` }}
                          />
                        </div>
                        <span className="text-sm">{systemStats.disk_percent.toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {workerStats && (
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Workers</p>
                    <p className="text-sm">
                      {workerStats.active_workers} / {workerStats.max_workers}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active Conferences */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-500" />
              Conferências Ativas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeConferences.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhuma conferência ativa
              </p>
            ) : (
              <div className="space-y-3">
                {activeConferences.map((conf, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">{conf.room_name || 'Reunião'}</p>
                      <p className="text-xs text-muted-foreground">
                        {conf.participants_count || 0} participantes
                      </p>
                    </div>
                    <Badge variant="info">Em andamento</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Processing Sessions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Em Processamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {processingSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhuma sessão sendo processada
              </p>
            ) : (
              <div className="space-y-3">
                {processingSessions.map((session, index) => (
                  <div key={index} className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{session.room_name || 'Reunião'}</p>
                      {session.phase && (
                        <Badge variant="secondary">{session.phase}</Badge>
                      )}
                    </div>
                    {session.progress !== undefined && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full">
                          <div
                            className="h-full bg-yellow-500 rounded-full transition-all"
                            style={{ width: `${session.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-12 text-right">
                          {Math.round(session.progress)}%
                        </span>
                      </div>
                    )}
                    {session.audio_duration_sec && (
                      <p className="text-xs text-muted-foreground">
                        Duração: {formatDuration(session.audio_duration_sec)}
                        {session.eta_minutes && ` • ETA: ${session.eta_minutes}min`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Waiting Queue */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Fila de Espera
            </CardTitle>
          </CardHeader>
          <CardContent>
            {waitingSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhuma sessão na fila
              </p>
            ) : (
              <div className="space-y-2">
                {waitingSessions.slice(0, 10).map((session, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground w-6">
                        #{index + 1}
                      </span>
                      <span className="text-sm">{session.room_name || 'Reunião'}</span>
                    </div>
                    {session.audio_duration_sec && (
                      <span className="text-xs text-muted-foreground">
                        {formatDuration(session.audio_duration_sec)}
                      </span>
                    )}
                  </div>
                ))}
                {waitingSessions.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    + {waitingSessions.length - 10} mais na fila
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Completions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Processadas Recentemente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhuma sessão processada recentemente
              </p>
            ) : (
              <div className="space-y-2">
                {recentSessions.slice(0, 10).map((session, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{session.room_name || 'Reunião'}</span>
                    </div>
                    {session.audio_duration_sec && (
                      <span className="text-xs text-muted-foreground">
                        {formatDuration(session.audio_duration_sec)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ETA Info */}
      {data?.eta_info && data.eta_info.total_waiting > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Estimativa de conclusão da fila</span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {formatDuration(data.eta_info.estimated_minutes * 60)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {data.eta_info.total_waiting} reuniões pendentes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
